import * as React from "react";
import { Flex } from "@chakra-ui/react";
import { Route, Routes, useLocation } from "react-router-dom";
import { casesActions } from "configs";
import { useDispatch, useOnline, useParams } from "duck";
import * as LC from "./components";
import {
  useCacheImages,
  useCaseConfig,
  useInitCaseData,
  useRealtime
} from "./duck";

const Case: React.FC = () => {
  const location = useLocation();
  const online = useOnline();

  const {
    normalStatus,
    hasCase,
    infoFilled,
    hasTplo,
    hasDentalCharting,
    shouldRefresh
  } = useCaseConfig();

  const { loading } = useInitCaseData(shouldRefresh);
  useRealtime({ initComplete: !loading });
  useCacheImages({ initComplete: !loading });

  const dispatch = useDispatch();
  const { id } = useParams();

  React.useEffect(() => {
    if (loading) {
      return;
    }

    /* Switch to the offline mode and prevent a user for sending any requests.  */
    if (!online && hasCase && shouldRefresh) {
      dispatch(casesActions.setShouldSync({ _id: id, data: false }));
    }
    if (online && hasCase && normalStatus) {
      /* If there were no changes done in the offline mode, switch back to the normal online mode. */
      dispatch(casesActions.setShouldSync({ _id: id, data: true }));
    }
  }, [online, hasCase, shouldRefresh, normalStatus, loading]);

  if (!hasCase || loading) {
    return null;
  }

  const customHeader =
    location.pathname.includes("emergency-drugs") ||
    location.pathname.match(/\/dental-charting\/\d/) ||
    location.pathname.includes("anesthetic-protocols");

  return (
    <Flex flex="1" flexDirection="column">
      {!customHeader && (
        <LC.Header
          allTabs={infoFilled}
          hasDentalCharting={hasDentalCharting}
          hasTplo={hasTplo}
        />
      )}
      <LC.MonitoringTimerModals />
      <Routes>
        <Route path="/check-in" element={<LC.CheckIn />} />
        {infoFilled && (
          <>
            <Route path="/emergency-drugs" element={<LC.EmergencyDrugs />} />
            <Route path="/exam" element={<LC.Exam />} />
            <Route path="/calculations" element={<LC.Calculation />} />
            <Route
              path="/calculations/anesthetic-protocols"
              element={<LC.Protocols />}
            />
            <Route path="/monitoring" element={<LC.Monitoring />} />
            <Route path="/prescriptions" element={<LC.Prescriptions />} />
            {hasTplo && <Route path="/tplo" element={<LC.TPLO />} />}
            {hasDentalCharting && (
              <Route path="/dental-charting" element={<LC.DentalCharting />} />
            )}
            {hasDentalCharting && (
              <Route
                path="/dental-charting/:toothNumber"
                element={<LC.ToothDetails />}
              />
            )}
          </>
        )}
      </Routes>
    </Flex>
  );
};

const Wrapper: React.FC = () => {
  const { id } = useParams();

  return <Case key={id} />;
};

export default Wrapper;
